/**
 * Marketing Lead Types
 *
 * Mirrors the data structure stored in valutten-prod Firestore
 * under marketing_leads/{type}/entries/{docId}
 */

// TODO: Build affiliate commission infrastructure on top of referralCode.
//       When affiliate program launches: affiliates collection, commission records,
//       payout tracking, approval workflow, fraud detection, ABN/GST/RCTI handling.
//       The referralCode field captured here is the foundation those systems will use.
export interface LeadSource {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referralCode?: string;
  referredBy?: string;
  landingPage?: string;
  formPage?: string;
  referrerUrl?: string;
  firstTouchSource?: string;
  firstTouchTimestamp?: string;
  lastTouchSource?: string;
}

export type LeadStatus = 'new' | 'contacted' | 'demo_booked' | 'demo_completed' | 'negotiating' | 'converted' | 'lost';
export type LeadType = 'scorecard' | 'founders' | 'contacts' | 'waitlist';

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  demo_booked: 'Demo Booked',
  demo_completed: 'Demo Completed',
  negotiating: 'Negotiating',
  converted: 'Converted',
  lost: 'Lost',
};

export const LEAD_STATUS_COLORS: Record<LeadStatus, string> = {
  new: '#3b82f6',
  contacted: '#8b5cf6',
  demo_booked: '#f59e0b',
  demo_completed: '#06b6d4',
  negotiating: '#f97316',
  converted: '#22c55e',
  lost: '#ef4444',
};

export const LEAD_TYPE_LABELS: Record<LeadType, string> = {
  scorecard: 'Scorecard',
  founders: 'Founder Applications',
  contacts: 'Contacts',
  waitlist: 'Waitlist',
};

export interface StatusChange {
  from: string | null;
  to: string;
  changedBy: string;
  changedAt: Date | { toDate: () => Date };
  note?: string;
}

export interface MarketingLead {
  id: string;
  type: LeadType;
  // Common fields
  email: string;
  source?: LeadSource;
  status: LeadStatus;
  statusHistory: StatusChange[];
  bookedDemo: boolean;
  converted: boolean;
  timestamp: Date | { toDate: () => Date };
  updatedAt?: Date | { toDate: () => Date };
  userAgent?: string;
  ipAddress?: string;
  notes?: string;
  // Scorecard-specific
  firstName?: string;
  lastName?: string;
  phone?: string;
  company?: string;
  answers?: Record<string, string>;
  score?: number;
  tier?: string;
  // Founder-specific
  fullName?: string;
  mobile?: string;
  companyName?: string;
  numberOfOffices?: string;
  numberOfBrokers?: string;
  brokerType?: string;
  annualLoanVolume?: string;
  currentTrackingMethod?: string;
  biggestChallenge?: string;
  consentContact?: boolean;
  subscribeUpdates?: boolean;
  // Contact-specific
  name?: string;
  message?: string;
}

export interface DashboardStats {
  totalLeads: number;
  leadsThisMonth: number;
  demosBooked: number;
  conversionRate: number;
  byStatus: Record<LeadStatus, number>;
  bySource: Record<string, number>;
  byType: Record<LeadType, number>;
  recentLeads: MarketingLead[];
}

/**
 * Get display name for a lead regardless of type
 */
export function getLeadDisplayName(lead: MarketingLead): string {
  if (lead.firstName) return `${lead.firstName} ${lead.lastName || ''}`.trim();
  if (lead.fullName) return lead.fullName;
  if (lead.name) return lead.name;
  return lead.email;
}

/**
 * Get company name for a lead regardless of type
 */
export function getLeadCompany(lead: MarketingLead): string {
  return lead.company || lead.companyName || '-';
}

/**
 * Safely convert Firestore timestamp to Date
 */
export function toDate(val: Date | { toDate: () => Date } | undefined): Date {
  if (!val) return new Date(0);
  if (val instanceof Date) return val;
  if (typeof (val as any).toDate === 'function') return (val as any).toDate();
  return new Date(val as any);
}
